// Owns exactly one service-worker registration for the document's lifetime, and
// is the composition root where every seam the PWA layer inverted gets its
// concrete adapter.
//
// Registration lives here rather than on `virtual:pwa-register/react` because
// the plugin's `autoUpdate` mode installs an unconditional
// `activated → window.location.reload()` listener with no opt-out — any
// safe-apply policy layered on top of it is decoration, since the plugin
// reloads first. `vite-plugin-pwa` stays the service-worker *builder*
// (`vite.config.js` is untouched); we own the *client*.
//
// It is initialized once from `main.tsx` before `root.render()`, so
// `<StrictMode>`'s double-invoke cannot create a second `Workbox`, and so it
// survives every `bootState` transition.

import { Workbox, type WorkboxLifecycleEvent } from "workbox-window";
import {
	getSnapshot as reachabilitySnapshot,
	subscribe as subscribeReachability,
} from "@/lib/offline/reachability";
import { captureError } from "@/sentry";
import { requestAppReload, setDeferredSince } from "./appReload";
import {
	clearDeferredSince,
	readDeferredSince,
	writeDeferredSince,
} from "./deferredUpdateStorage";
import { getFormDirtiness, subscribeDirtyForms } from "./dirtyFormRegistry";
import {
	getPendingWork,
	startPendingWorkSnapshot,
	subscribePendingWork,
} from "./pendingWorkSnapshot";
import {
	ServiceWorkerUpdateCheckError,
	startUpdateTriggers,
	type UpdateSignal,
} from "./swUpdateTriggers";
import { APPLY_ESCALATION_MS, decideUpdateApply } from "./updateApplyPolicy";

export type UpdateState =
	| "current"
	| "deferred-user-busy"
	| "deferred-work-pending"
	| "applying";

const SW_SCRIPT_URL = "/sw.js";

/**
 * Last-resort trigger for a document nobody interacts with and whose network
 * state never flips. The scheduler's own 5-minute floor is what bounds the
 * request budget; this interval only guarantees the floor is reachable.
 */
const BACKSTOP_INTERVAL_MS = 30 * 60_000;

/**
 * The service-worker lifecycle exposes no build identifier, so the deferral
 * deadline is keyed by the incoming worker's script URL plus a per-document
 * boot token. A deadline written by a *previous* document in this tab is stale
 * by construction — that document either reloaded (applying the update) or was
 * discarded — and the revision mismatch makes `readDeferredSince` drop it.
 */
const BOOT_TOKEN = Date.now().toString(36);

type Listener = () => void;

let initialized = false;
let workbox: Workbox | null = null;
let registrationPromise: Promise<ServiceWorkerRegistration | undefined> | null =
	null;
let disposeTriggers: (() => void) | null = null;
let disposePendingWork: (() => void) | null = null;
let disposeSafetyInputs: (() => void)[] = [];
let escalationTimer: ReturnType<typeof setTimeout> | null = null;

let updatePending = false;
let firstController: ServiceWorker | null = null;
let swRevision = `${SW_SCRIPT_URL}#${BOOT_TOKEN}`;
let updateState: UpdateState = "current";
const listeners = new Set<Listener>();

function notify() {
	for (const fn of listeners) fn();
}

function setUpdateState(next: UpdateState) {
	if (next === updateState) return;
	updateState = next;
	notify();
}

/** The Sentry-backed adapter for the scheduler's injected failure reporter. */
function recordCheckFailure(error: ServiceWorkerUpdateCheckError): void {
	captureError(error, { tags: { "sw.update.check": "failed" } });
}

/**
 * One `registration.update()` per check and nothing else. The vite-pwa periodic
 * recipe puts a `fetch(swUrl, {cache:"no-store"})` in front of this to avoid
 * calling `update()` when the server is unreachable — a named deviation: per
 * MDN `update()` already performs exactly that conditional fetch, so the
 * preflight doubled the request budget for nothing, and an unreachable server
 * is already handled by the scheduler catching the rejection.
 */
async function checkForUpdate(): Promise<void> {
	const registration = await registrationPromise;
	if (!registration) return;
	await registration.update();
}

function clearEscalationTimer() {
	if (escalationTimer === null) return;
	clearTimeout(escalationTimer);
	escalationTimer = null;
}

/**
 * `decideUpdateApply` is pure over `now`, so nothing re-runs it when the clock
 * is the only input that moved. Without this timer a clean, visible client
 * holding a persistently non-empty queue — the offline field user, i.e. the
 * user the deadline exists for — would never re-evaluate and
 * `APPLY_ESCALATION_MS` would bound nothing.
 *
 * Replaced, never stacked: each call clears the previous one-shot first. Past
 * the deadline no timer is armed, because the clock can no longer change the
 * verdict and a zero-delay re-arm would spin.
 */
function scheduleEscalation(deferredSinceMs: number, now: number) {
	clearEscalationTimer();
	const delay = deferredSinceMs + APPLY_ESCALATION_MS - now;
	if (delay <= 0) return;
	escalationTimer = setTimeout(() => {
		escalationTimer = null;
		evaluateUpdate();
	}, delay);
}

/**
 * Re-derive the update state from the current safety inputs, and request the
 * reload when they permit one. Driven by service-worker activation, by both
 * safety stores, by the escalation one-shot, and by visibility resume.
 *
 * Deliberately does not short-circuit on `applying`: the arbiter re-checks
 * after awaiting the queue to settle and may decline, and the store
 * notification that follows is what moves the state back off `applying`.
 */
function evaluateUpdate(): void {
	if (!updatePending) {
		if (updateState === "current") return;
		clearEscalationTimer();
		clearDeferredSince();
		setDeferredSince(null);
		setUpdateState("current");
		return;
	}

	const now = Date.now();
	const storedDeferredSince = readDeferredSince(swRevision);
	const decision = decideUpdateApply({
		pendingWork: getPendingWork(),
		formDirtiness: getFormDirtiness(),
		deferredSinceMs: storedDeferredSince,
		now,
	});

	if (decision === "apply-now") {
		clearEscalationTimer();
		setDeferredSince(storedDeferredSince);
		setUpdateState("applying");
		void requestAppReload("sw-activated");
		return;
	}

	// Start the deferral clock on the first refusal, then keep it: the deadline
	// is measured from when the update became available, not from the last
	// re-evaluation, or a busy client would push it forward forever.
	const deferredSince = storedDeferredSince ?? now;
	if (storedDeferredSince === null)
		writeDeferredSince(swRevision, deferredSince);
	setDeferredSince(deferredSince);
	scheduleEscalation(deferredSince, now);
	setUpdateState(
		decision === "defer-work-pending"
			? "deferred-work-pending"
			: "deferred-user-busy",
	);
}

function onInstalled(event: WorkboxLifecycleEvent): void {
	// Earliest point the incoming worker is identifiable. Recording it here
	// scopes the deferral clock to *this* update rather than to any earlier one.
	// Installation alone does not make the new precache live, so nothing is
	// applied yet — `activated`/`controlling` own that.
	if (event.sw?.scriptURL) swRevision = `${event.sw.scriptURL}#${BOOT_TOKEN}`;
}

function onUpdateLive(event: WorkboxLifecycleEvent): void {
	// A document that loaded without a controller came from the network, so the
	// first worker to take control serves the build it already runs, and
	// reloading would abort whatever the user just started. Workbox reports that
	// worker as external when another page load started installing it, and a
	// first install claims the page inside `activate`: `controlling` arrives
	// before the same worker's `activated`, so both events are ignored. An
	// external `activated` that arrives before `controlling` can't be told from
	// an update to a document that booted controlled, so it still reloads. A
	// document that stays uncontrolled across a deploy accepts that its first
	// controller may be a newer build.
	if (!event.isUpdate && isFirstControllerEvent(event)) return;
	updatePending = true;
	evaluateUpdate();
}

function isFirstControllerEvent(event: WorkboxLifecycleEvent): boolean {
	const controller = navigator.serviceWorker.controller;
	if (event.type === "controlling") {
		firstController ??= controller;
		return controller === firstController;
	}
	if (firstController === null) return !event.isExternal;
	return event.sw === firstController;
}

const visibilitySignal: UpdateSignal = (fire) => {
	const onVisibilityChange = () => {
		if (document.visibilityState !== "visible") return;
		fire();
		// A frozen tab runs no timers, so it can wake past the escalation
		// deadline with the one-shot never having fired. Re-check on resume.
		evaluateUpdate();
	};
	document.addEventListener("visibilitychange", onVisibilityChange);
	return () =>
		document.removeEventListener("visibilitychange", onVisibilityChange);
};

const reachabilitySignal: UpdateSignal = (fire) =>
	subscribeReachability(() => {
		// Only a transition back *online* can produce a check that succeeds.
		if (reachabilitySnapshot()) fire();
	});

const backstopSignal: UpdateSignal = (fire) => {
	const handle = setInterval(fire, BACKSTOP_INTERVAL_MS);
	return () => clearInterval(handle);
};

/**
 * Idempotent by construction: repeated calls (React remounts, a second entry
 * point, `<StrictMode>`) return without constructing a second `Workbox` or
 * binding a second copy of any listener.
 */
export function initServiceWorkerClient(): void {
	if (initialized) return;
	initialized = true;

	// `vite-plugin-pwa` builds no service worker in dev (`devOptions` is off), so
	// `/sw.js` there resolves to the SPA fallback HTML and registration fails on
	// every boot.
	if (import.meta.env.DEV) return;
	if (typeof navigator === "undefined" || !("serviceWorker" in navigator))
		return;

	const wb = new Workbox(SW_SCRIPT_URL);
	workbox = wb;
	wb.addEventListener("installed", onInstalled);
	wb.addEventListener("activated", onUpdateLive);
	wb.addEventListener("controlling", onUpdateLive);

	registrationPromise = wb.register().catch((error: unknown) => {
		recordCheckFailure(new ServiceWorkerUpdateCheckError(error));
		return undefined;
	});

	disposePendingWork = startPendingWorkSnapshot();
	disposeSafetyInputs = [
		subscribePendingWork(evaluateUpdate),
		subscribeDirtyForms(evaluateUpdate),
	];

	disposeTriggers = startUpdateTriggers({
		signals: [visibilitySignal, reachabilitySignal, backstopSignal],
		checkForUpdate,
		recordCheckFailure,
		now: () => Date.now(),
	});
}

export function getUpdateState(): UpdateState {
	return updateState;
}

export function subscribeUpdateState(listener: Listener): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

/**
 * The user pressed "Atualizar agora". A *request*, never an override: it routes
 * through the same arbiter, so the "never reload a dirty form" invariant holds
 * even against an explicit user action. The honest path out of a dirty form is
 * to finish or discard it, at which point the state flips on its own.
 */
export function requestApplyUpdate(): void {
	if (!updatePending) return;
	// The policy may have flipped to apply-now since the banner rendered, in
	// which case `evaluateUpdate` issues the request itself.
	evaluateUpdate();
	if (updateState === "deferred-user-busy")
		void requestAppReload("sw-activated");
}

// Exposed for unit tests only — allows resetting module state between test cases.
export function _resetForTesting() {
	clearEscalationTimer();
	disposeTriggers?.();
	disposePendingWork?.();
	for (const dispose of disposeSafetyInputs) dispose();
	disposeTriggers = null;
	disposePendingWork = null;
	disposeSafetyInputs = [];
	if (workbox) {
		workbox.removeEventListener("installed", onInstalled);
		workbox.removeEventListener("activated", onUpdateLive);
		workbox.removeEventListener("controlling", onUpdateLive);
		workbox = null;
	}
	registrationPromise = null;
	initialized = false;
	updatePending = false;
	firstController = null;
	updateState = "current";
	swRevision = `${SW_SCRIPT_URL}#${BOOT_TOKEN}`;
	listeners.clear();
}
